import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LocaleProvider } from "./i18n";
import { ConciergeProvider } from "./lib/concierge";
import { Toaster } from "sonner";
import { trackPageView } from "./lib/analytics";

import Header from "./components/Header";
import Footer from "./components/Footer";
import ConciergeWidget from "./components/ConciergeWidget";
import SkipLink from "./components/SkipLink";

import Home from "./pages/Home";
import { RegionsList } from "./pages/Regions";
import RegionDetailFull from "./pages/RegionDetailFull";
import ListingDetail from "./pages/ListingDetail";
import AdminDashboard from "./pages/admin/AdminDashboard";
import ConciergePage from "./pages/Concierge";
import ToolsIndex from "./pages/ToolsIndex";
import CurrencyTool from "./pages/tools/Currency";
import DistanceTool from "./pages/tools/Distance";
import BudgetTool from "./pages/tools/Budget";
import WeatherTool from "./pages/tools/Weather";
import PhrasebookTool from "./pages/tools/Phrasebook";
import About from "./pages/About";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsOfUse from "./pages/legal/TermsOfUse";
import CookiePolicy from "./pages/legal/CookiePolicy";
import WowJourney from "./pages/wow-journey/WowJourney";
import { FEATURE_ENABLED as WJ_ENABLED } from "./lib/wowJourneyApi";

function Layout({ children }) {
  const location = useLocation();
  const hideWidget = location.pathname.startsWith("/concierge");
  return (
    <div className="App grain min-h-screen bg-[var(--wr-bg)] text-[var(--wr-ink)]">
      <SkipLink />
      <Header />
      <main id="main-content" tabIndex={-1} data-testid="main-content">{children}</main>
      <Footer />
      {!hideWidget && <ConciergeWidget />}
      <Toaster position="bottom-center" richColors />
    </div>
  );
}

// GA4 SPA page_view tracker. Fires on initial mount and every route change.
// Uses a tiny timeout so document.title (set per-page by react-helmet-async)
// has settled before we send the event.
function RouteTracker() {
  const location = useLocation();
  React.useEffect(() => {
    const t = setTimeout(() => trackPageView(location.pathname, location.search), 0);
    return () => clearTimeout(t);
  }, [location.pathname, location.search]);
  return null;
}

function App() {
  return (
    <LocaleProvider>
      <ConciergeProvider>
      <BrowserRouter>
        <RouteTracker />
        <Layout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/regions" element={<RegionsList />} />
            <Route path="/regions/:slug" element={<RegionDetailFull />} />
            <Route path="/listings/:slug" element={<ListingDetail />} />
            <Route path="/admin/*" element={<AdminDashboard />} />
            <Route path="/tools" element={<ToolsIndex />} />
            <Route path="/tools/currency" element={<CurrencyTool />} />
            <Route path="/tools/distance" element={<DistanceTool />} />
            <Route path="/tools/budget" element={<BudgetTool />} />
            <Route path="/tools/weather" element={<WeatherTool />} />
            <Route path="/tools/phrasebook" element={<PhrasebookTool />} />
            <Route path="/concierge" element={<ConciergePage />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfUse />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            {WJ_ENABLED && <Route path="/plan" element={<WowJourney />} />}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
      </BrowserRouter>
      </ConciergeProvider>
    </LocaleProvider>
  );
}

export default App;
